import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { ArrowRight, BadgeCheck, FileText } from "lucide-react";
import { motion } from "framer-motion";
import {
  FaShieldAlt, // For Zero Depreciation Cover
  FaTools, // For Engine and Gearbox Protection
  FaFileInvoiceDollar, // For Return to Invoice
  FaTruckPickup, // For Roadside Assistance
  FaOilCan, // For Consumables Cover
  FaCarSide, // For Tyre and Rim Protection
  FaKey, // For Key Replacement Cover
  FaCloudSunRain,
  FaUserLock,
  FaCarCrash,
} from "react-icons/fa";
import FAQSchema from "../../FAQSchema";
import AdBanner from "../AdPage/AdBanner";

function Icon({ id, open }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      className={`${
        id === open ? "rotate-180" : ""
      } h-5 w-5 transition-transform`}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
      />
    </svg>
  );
}

const BMWCarInsurance = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [insuranceData, setInsuranceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const faqs = [
    {
      question: "Is zero depreciation cover essential for BMW cars?",
      answer: "Yes, especially within the first 4–5 years of ownership.",
    },
    {
      question: "Can I insure a second-hand BMW?",
      answer: "Absolutely. A pre-inspection by the insurer may be required.",
    },
    {
      question: "Do BMW electric models like iX and i4 get special coverage?",
      answer:
        "Yes, EV-specific plans cover battery and charging infrastructure.",
    },
    {
      question: "How long does it take to settle a BMW insurance claim?",
      answer: "Typically within 7–12 working days, depending on documentation.",
    },
    {
      question: "Is engine protection worth it for BMW cars?",
      answer: "Definitely, given the complexity and cost of BMW engines.",
    },
    {
      question: "What happens if I modify my BMW (like installing M kits)?",
      answer:
        "You should inform your insurer; it may affect premiums or claim eligibility.",
    },
    {
      question:
        "Are BMW cars expensive to insure compared to Mercedes or Audi?",
      answer: "Generally in the same bracket, depending on the model and age.",
    },
    {
      question: "Can I get cashless repairs at a BMW service center?",
      answer:
        "Yes, with most insurers offering cashless facilities at authorized outlets.",
    },
    {
      question: "Does NCB transfer apply from another luxury brand?",
      answer: "Yes, as long as the ownership is the same.",
    },
    {
      question: "Are there discounts for bundling multiple BMW cars?",
      answer:
        "Some insurers provide multi-vehicle discounts for the same policyholder.",
    },
    {
      question: "Is roadside assistance included by default?",
      answer:
        "It’s usually an optional add-on, but some luxury plans include it.",
    },
    {
      question: "Do I need a separate policy for accessories?",
      answer:
        "Yes, if they are expensive aftermarket parts, declare them separately.",
    },
    {
      question: "What is Return to Invoice (RTI)?",
      answer:
        "An add-on that pays you the full invoice amount in case of total loss or theft.",
    },
    {
      question: "Can I buy BMW insurance fully online?",
      answer: "Yes, many insurers offer 100% online issuance and renewal.",
    },
    {
      question: "What documents are needed for a claim?",
      answer:
        "RC, insurance policy, driving license, FIR (if applicable), repair bills.",
    },
    {
      question: "Does insurance cover minor dents and scratches?",
      answer: "Only if they occur due to an insured event like an accident.",
    },
    {
      question: "Will my insurance lapse if I delay renewal?",
      answer:
        "After 90 days, NCB benefits are lost. Renew on time to avoid this.",
    },
    {
      question: "Are BMW EVs more expensive to insure?",
      answer:
        "Slightly higher premiums due to battery cost and tech sensitivity.",
    },
    {
      question: "Can I customize add-ons during renewal?",
      answer:
        "Yes, every renewal is a chance to tailor your plan based on usage.",
    },
    {
      question: "Which add-on is most useful for highway driving?",
      answer:
        "Engine protection and roadside assistance are highly recommended.",
    },
  ];

  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const cardsNew = [
    {
      title: "Zero Depreciation Cover",
      description: "Eliminates part depreciation during claims.",
      icon: <FaShieldAlt className="text-4xl text-red-500 mb-4" />,
    },
    {
      title: "Engine and Gearbox Protection",
      description: "Vital for turbocharged engines and monsoon-prone cities.",
      icon: <FaTools className="text-4xl text-yellow-600 mb-4" />,
    },
    {
      title: "Return to Invoice",
      description:
        "Especially helpful for new cars to recover full purchase value in case of theft or total damage.",
      icon: <FaFileInvoiceDollar className="text-4xl text-orange-600 mb-4" />,
    },
    {
      title: "Tyre and Rim Protection",
      description: "BMW wheels are high-performance and expensive to replace.",
      icon: <FaCarSide className="text-4xl text-indigo-600 mb-4" />,
    },
    {
      title: "Roadside Assistance",
      description:
        "Includes towing, jump-start, emergency fuel and on-spot repair.",
      icon: <FaTruckPickup className="text-4xl text-blue-500 mb-4" />,
    },
    {
      title: "Consumables Cover",
      description:
        "Includes oils, lubricants, coolants, and fasteners used in repairs.",
      icon: <FaOilCan className="text-4xl text-green-600 mb-4" />,
    },
    {
      title: "Key Replacement Cover",
      description: "Useful due to high-tech digital key fobs in modern BMWs.",
      icon: <FaKey className="text-4xl text-purple-600 mb-4" />,
    },
  ];

  const features = [
    { icon: "🛡️", text: "Reliable & Accurate Estimates" },
    { icon: "⏱️", text: "Takes Less Than a Minute" },
    { icon: "📉", text: "Instantly Shows Premium Breakup" },
    { icon: "📄", text: "No Paperwork or Documentation" },
    { icon: "📲", text: "Easy to Use on Any Device" },
    { icon: "💰", text: "Helps You Save on Insurance" },
  ];
const bmwinsurance = [
    {
      name: "X1",
      path: "/bmw-x1-car-insurance",
      image: "/assets/bmwx1.jpeg",
    },
      {
      name: "3 Series",
      path: "/bmw-3series-car-insurance",
      image: "/assets/bmw3series.jpeg",
    },
       {
      name: "5 Series",
      path: "/bmw-5series-car-insurance",
      image: "/assets/bmw5series.jpeg",
    },
       {
      name: "7 Series",
      path: "/bmw-7series-car-insurance",
      image: "/assets/bmw7series.jpeg",
    },
       {
      name: "X3",
      path: "/bmw-x3-car-insurance",
      image: "/assets/bmwx3.jpeg",
    },
     {
      name: "X5",
      path: "/bmw-x5-car-insurance",
      image: "/assets/bmwx5.jpeg",
    },
    {
      name: "iX",
      path: "/bmw-ix-car-insurance",
      image: "/assets/bmwix.jpeg",
    },
     {
      name: "Z4",
      path: "/bmw-z4-car-insurance",
      image: "/assets/bmwz4.jpeg",
    },
  ];
  return (
    <>
      <Helmet>
        <title>BMW Car Insurance in India – Compare Quotes & Buy Online</title>
        <meta
          name="description"
          content="Explore BMW car insurance options in India. Compare quotes, customize coverage, and buy online to protect your BMW vehicle."
        />
        <meta
          name="keywords"
          content="bmw car insurance, luxury car insurance india, bmw 3 series insurance, bmw x5 insurance, bmw ix insurance, car insurance quotes"
        />
        <link
          rel="canonical"
          href="https://thefinmaster.com/bmw-car-insurance"
        />
        <meta
          name="robots"
          content="max-snippet:-1, max-image-preview:standard"
        />
        <meta name="robots" content="max-video-preview:-1" />
       
      </Helmet>
      <FAQSchema faqs={faqs} />
      <div className="w-full flex justify-center items-center px-4 mt-20">
        <div className="w-full max-w-5xl h-auto object-contain rounded-lg shadow-md transition duration-300 hover:scale-105">
          <AdBanner displayOrder="1" />
        </div>
      </div>
      <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800">
              Home
            </Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <span className="text-gray-500">BMW Car Insurance</span>
          </li>
        </ol>
      </nav>
      <div className="w-full flex flex-col items-center">
        <div className="w-full flex justify-center">
          <div className="w-full sm:w-[95%] flex flex-col sm:flex-row justify-center items-center">
            <div className="w-full sm:w-[50%]">
              <div className="w-[90%]">
                <h1 className="text-3xl md:text-5xl font-bold mt-6 md:mt-0 md:pl-4 pl-6">
                  <span className="text-blue-600">BMW Car Insurance Guide</span>{" "}
                  You Need to Know
                </h1>
                <p className="text-lg md:text-base lg:text-lg mt-4 pl-4">
                BMW is known for blending luxury, innovation, and driving pleasure in every model. Whether you own a sporty 3 Series or a powerful X5, keeping your car protected starts with choosing the right insurance. This guide walks you through everything you need to know about insuring a BMW, from average premiums and model insights to useful add-ons, renewal tips, and frequently asked questions.

                </p>
              </div>
            </div>
            <div className="w-full sm:w-[50%]">
              <img
                className="w-full max-w-full mt-2"
                src="../assets/bmwinsurance.jpeg"
                alt="BMW Car"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="flex justify-center items-center p-4 mt-6">
        <div className="bg-white shadow-lg rounded-2xl p-6 max-w-7xl text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">
            About BMW in India
          </h2>
          <p className="text-gray-600 leading-relaxed">
            BMW has carved a strong niche in India’s luxury automotive market.
            The German brand is favored by professionals, entrepreneurs and
            enthusiasts who seek driving pleasure with luxury features. With
            local assembly of several models and a growing EV lineup, BMW has
            expanded its reach in both metros and tier-1 cities.
          </p>
          <p className="text-gray-600 leading-relaxed mt-3">
            Popular models in India include the 2 Series Gran Coupé, 3 Series,
            X1, X3, X5, and the all-electric iX and i4. BMW also offers
            performance variants under the M badge for a sportier experience.
            Given the value and complexity of these vehicles, comprehensive car
            insurance is crucial for peace of mind.
          </p>
        </div>
      </div>
       <div className="p-4 sm:p-8 bg-white">
              <h2 className="text-xl sm:text-2xl font-bold mb-6 text-center">
                Choose Your Car Brand
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {bmwinsurance.map((calc, index) => (
                  <motion.div
                    key={index}
                    whileHover={{ scale: 1.04 }}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                  >
                    <Link
                      to={calc.path}
                      className="flex flex-col items-center justify-center text-center border border-gray-200 rounded-lg p-5 bg-white shadow-sm hover:shadow-md transition-all duration-300 h-full min-h-[160px]"
                    >
                      <img
                        src={calc.image}
                        alt={calc.name}
                        className="w-16 h-16 object-contain mb-2"
                      />
                      <span className="text-md font-medium text-gray-800 break-words leading-snug">
                        {calc.name}
                      </span>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="2" />
        </div>
      </div>
      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        <div className="absolute inset-0 z-0 pointer-events-none">
          <div className="absolute top-[-10%] left-[-10%] w-96 h-96 bg-blue-400 opacity-20 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-400 opacity-20 blur-3xl rounded-full"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-5xl mx-auto bg-white/90 backdrop-blur-lg rounded-2xl shadow-xl p-10"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
            Why BMW Owners Need Specialized Car Insurance
          </h2>
          <p className="text-gray-700 text-lg leading-relaxed mb-6">
            BMW cars come equipped with advanced driver assistance systems,
            premium infotainment units, adaptive suspensions and
            high-performance engines. These features enhance safety and comfort
            but also lead to higher repair and replacement costs. Here’s why you
            need robust insurance for your BMW:
          </p>
          <ul className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 text-gray-700 mb-6">
            <li className="flex items-start gap-3">
              <FaCarCrash className="text-xl text-red-500 mt-1" />
              <span>
                Protection from road accidents, theft and natural disasters
              </span>
            </li>
            <li className="flex items-start gap-3">
              <FaUserLock className="text-xl text-yellow-500 mt-1" />
              <span>Coverage for expensive OEM parts and electronics</span>
            </li>
            <li className="flex items-start gap-3">
              <FaCloudSunRain className="text-xl text-blue-500 mt-1" />
              <span>Cashless servicing at authorized BMW repair centers</span>
            </li>
            <li className="flex items-start gap-3">
              <FaShieldAlt className="text-xl text-green-500 mt-1" />
              <span>Peace of mind for high-speed or long-distance travel</span>
            </li>
            <li className="flex items-start gap-3">
              <FaTools className="text-xl text-indigo-500 mt-1" />
              <span>Legal and third-party liability coverage</span>
            </li>
          </ul>
          <div className="bg-indigo-50 border-l-4 border-indigo-400 p-4 rounded-lg text-indigo-700">
            Premium insurance also allows you to claim without hassles and
            ensures access to high-quality parts and trained technicians.
          </div>
        </motion.div>
      </section>
      <section className="bg-blue-50 py-6 px-4 sm:px-6 md:px-20 lg:px-20 rounded-3xl shadow-lg max-w-6xl mx-auto mt-10">
        <div className="flex flex-col md:flex-row justify-between items-center gap-10">
          <div className="max-w-xl space-y-6 text-center md:text-left">
            <h2 className="text-2xl sm:text-3xl font-bold text-blue-900 leading-tight">
              Calculate Your Car Insurance <br />
              in <span className="text-blue-600">Seconds</span>
            </h2>
            <p className="text-gray-700 text-lg">
              Use our fast and free calculator to estimate your premium — no
              paperwork, no waiting.
            </p>
            <Link
              to="/financial-tools-calculators/car-insurance-calculator"
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 text-lg rounded-full shadow-md transition"
            >
              Start Calculating <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <div className="space-y-4 text-left">
            {features.map((item, index) => (
              <div key={index} className="flex items-center gap-3">
                <span className="text-xl" role="img" aria-label={item.text}>
                  {item.icon}
                </span>
                <span className="text-gray-800 font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
      <div className="container mx-auto p-6 mt-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          BMW Models and Estimated Insurance Premiums
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Here’s a breakdown of some of the most popular BMW models in India,
          their estimated Insured Declared Value (IDV), and approximate starting
          insurance premiums:
        </p>
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="block md:hidden">
            {[
              {
                model: "2 Series Gran Coupé",
                category: "Entry-level Luxury",
                idv: "45–55 Lakh",
                premium: "₹35,000 to ₹42,000",
              },
              {
                model: "3 Series",
                category: "Executive Sedan",
                idv: "55–70 Lakh",
                premium: "₹40,000 to ₹55,000",
              },
              {
                model: "X1",
                category: "Compact SUV",
                idv: "50–65 Lakh",
                premium: "₹38,000 to ₹50,000",
              },
              {
                model: "5 Series",
                category: "Luxury Sedan",
                idv: "75 Lakh–90 Lakh",
                premium: "₹48,000 to ₹65,000",
              },
              {
                model: "X3",
                category: "Mid-size SUV",
                idv: "75 Lakh–95 Lakh",
                premium: "₹50,000 to ₹68,000",
              },
              {
                model: "X5",
                category: "Premium SUV",
                idv: "1 Cr–1.2 Cr",
                premium: "₹65,000 to ₹85,000",
              },
              {
                model: "iX / i4 / i7",
                category: "Electric Vehicles",
                idv: "70 Lakh–2 Cr+",
                premium: "₹60,000 to ₹1.3 Lakh",
              },
              {
                model: "M340i / M Series",
                category: "Performance Models",
                idv: "80 Lakh–2.5 Cr+",
                premium: "₹75,000 to ₹1.6 Lakh",
              },
            ].map((item, index) => (
              <div
                key={index}
                className="border mb-4 p-4 rounded-lg bg-white shadow-md"
              >
                <h3 className="font-semibold text-gray-800 mb-2">
                  {item.model}
                </h3>
                <div className="text-gray-600 mb-1">
                  <strong>Category:</strong> {item.category}
                </div>
                <div className="text-gray-600 mb-1">
                  <strong>Approx. IDV:</strong> {item.idv}
                </div>
                <div className="text-gray-600">
                  <strong>Starting Premium:</strong> {item.premium}
                </div>
              </div>
            ))}
          </div>
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full border-collapse border border-gray-300">
              <thead className="bg-gray-100 text-gray-800">
                <tr>
                  <th className="p-3 border border-gray-300 text-left">
                    Model
                  </th>
                  <th className="p-3 border border-gray-300 text-left">
                    Category
                  </th>
                  <th className="p-3 border border-gray-300 text-left">
                    Approx. IDV (₹)
                  </th>
                  <th className="p-3 border border-gray-300 text-left">
                    Starting Premium (₹)
                  </th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                {[
                  [
                    "2 Series Gran Coupé",
                    "Entry-level Luxury",
                    "45–55 Lakh",
                    "₹35,000 to ₹42,000",
                  ],
                  [
                    "3 Series",
                    "Executive Sedan",
                    "55–70 Lakh",
                    "₹40,000 to ₹55,000",
                  ],
                  ["X1", "Compact SUV", "50–65 Lakh", "₹38,000 to ₹50,000"],
                  [
                    "5 Series",
                    "Luxury Sedan",
                    "75 Lakh–90 Lakh",
                    "₹48,000 to ₹65,000",
                  ],
                  [
                    "X3",
                    "Mid-size SUV",
                    "75 Lakh–95 Lakh",
                    "₹50,000 to ₹68,000",
                  ],
                  ["X5", "Premium SUV", "1 Cr–1.2 Cr", "₹65,000 to ₹85,000"],
                  [
                    "iX / i4 / i7",
                    "Electric Vehicles",
                    "70 Lakh–2 Cr+",
                    "₹60,000 to ₹1.3 Lakh",
                  ],
                  [
                    "M340i / M Series",
                    "Performance Models",
                    "80 Lakh–2.5 Cr+",
                    "₹75,000 to ₹1.6 Lakh",
                  ],
                ].map(([model, category, idv, premium], index) => (
                  <tr key={index} className="border border-gray-300">
                    <td className="p-3 border border-gray-300">{model}</td>
                    <td className="p-3 border border-gray-300">{category}</td>
                    <td className="p-3 border border-gray-300">{idv}</td>
                    <td className="p-3 border border-gray-300">{premium}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className="container mx-auto p-6 mt-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Recommended Add-Ons for BMW Car Insurance
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          BMWs are engineered with complexity and sophistication. To ensure
          complete protection, these insurance add-ons are highly recommended:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mt-4">
          {cardsNew.map((card, index) => (
            <div
              key={index}
              className="border rounded-lg shadow-lg p-6 flex flex-col items-center text-center"
            >
              {card.icon}
              <h3 className="text-lg font-semibold mb-2">{card.title}</h3>
              <p className="text-gray-600">{card.description}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="3" />
        </div>
      </div>
      <div className="mx-auto container p-6 mt-6">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Top Insurers for BMW Car Insurance in India
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Certain insurance providers offer tailored plans for luxury car
          owners. These are ideal for BMW vehicles due to their claim efficiency
          and luxury garage network:
        </p>
        <div className="mt-6 grid md:grid-cols-3 gap-6 text-left">
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">⚡</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              Tata AIG
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Known for high-value car plans with robust add-on options.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🏥</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              HDFC ERGO
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Offers wide cashless garage access including premium brands.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🚗</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              ICICI Lombard
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Strong digital claim process and high coverage limits.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">💰</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              Bajaj Allianz
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Popular for luxury and imported car segments.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">📱</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">ACKO</h3>
            <p className="text-gray-700 text-sm mt-1">
              Simplified digital experience, ideal for EVs and quick issuance.
            </p>
          </div>
        </div>
      </div>
      <div className="w-full px-2 py-6 bg-gradient-to-br from-sky-50 via-white to-sky-100 dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-6xl mx-auto grid gap-10">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-sky-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-8 h-8 text-sky-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                Tips to Save on BMW Insurance Premium
              </span>
            </div>
            <h3 className="text-xl md:text-xl font-bold text-gray-800 dark:text-white mb-6">
              Luxury car premiums are higher, but here are smart ways to reduce
              costs without compromising coverage:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Compare policies online before renewal.</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Preserve your No Claim Bonus (NCB).</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>
                  Opt for voluntary deductibles if you’re a safe and experienced
                  driver.
                </span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>
                  Install anti-theft devices and GPS tracking systems.
                </span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Avoid claiming minor damages to retain discounts.</span>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-emerald-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-6">
              <BadgeCheck className="w-8 h-8 text-emerald-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                Documents Required for BMW Car Insurance
              </span>
            </div>
            <h3 className="text-xl md:text-xl font-bold text-gray-800 dark:text-white mb-6">
              Whether it’s a renewal or a new purchase, you’ll need the
              following:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Copy of Registration Certificate (RC)</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Previous insurance details (if renewing)</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>PAN and Aadhaar card</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Valid PUC certificate</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl sm:col-span-2">
                <span>
                  Photographs (optional for online, mandatory for offline in
                  some cases)
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto py-6 px-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-center mb-8">
          BMW Car Insurance FAQs
        </h2>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border rounded-lg overflow-hidden shadow-md"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
                aria-expanded={activeIndex === index}
                aria-controls={`faq-answer-${index}`}
              >
                {faq.question}
                <Icon id={index} open={activeIndex} />
              </button>
              {activeIndex === index && (
                <div id={`faq-answer-${index}`} className="p-4 bg-gray-100">
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none z-0">
          <div className="absolute top-[-20%] left-[-10%] w-96 h-96 bg-indigo-300 opacity-30 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-300 opacity-30 blur-3xl rounded-full"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-4xl mx-auto bg-white/90 backdrop-blur-sm border border-gray-200 shadow-xl rounded-2xl p-10"
        >
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="text-indigo-600 text-5xl">
              <FaCarSide />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Conclusion
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed mb-4">
                Owning a BMW is about more than just driving — it’s an
                experience. From the powerful M340i to the sustainable i4, each
                model deserves strong protection. With the right insurance
                policy, you ensure worry-free drives, hassle-free claims and
                complete financial protection.
              </p>
              <p className="text-gray-700 text-lg leading-relaxed">
                At TheFinMaster, we help you compare insurers, pick the right
                add-ons and secure your BMW with a plan tailored to your
                lifestyle and driving habits.
              </p>
            </div>
          </div>
        </motion.div>
      </section>
    </>
  );
};

export default BMWCarInsurance;
